import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Chip, Stack, Divider,
} from '@mui/material';
import { X, GraduationCap, ClipboardList } from 'lucide-react';
import { splitPassage } from '../Instructor/assignment/passageFormat';

/**
 * A reading passage: its title in bold, set apart from the body, then the body.
 * The title is the first line of the passage text (see splitPassage) and is the
 * name every question refers to it by.
 */
export function PassageBlock({ passage, caption }) {
  const { title, body } = splitPassage(passage);
  return (
    <Box sx={{ mt: 1.5, p: 1.25, borderRadius: 1, bgcolor: 'action.hover' }}>
      {caption && (
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.25 }}>
          {caption}
        </Typography>
      )}
      {title && (
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 0.5, mb: 1.25 }}>
          {title}
        </Typography>
      )}
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {body}
      </Typography>
    </Box>
  );
}

// Friendly label for the question's category (the new questionType field).
// Falls back to mimeType/type for older drafts that predate questionType.
const QUESTION_TYPE_LABELS = {
  Passage: 'Passage question',
  Scenario: 'Scenario question',
  Direct: 'Direct',
  Image: 'Image',
};
const typeOf = (q) => {
  const t = q.questionType || q.mimeType || q.type || 'Direct';
  return QUESTION_TYPE_LABELS[t] || t;
};
const sharedPassageOf = (assessment) =>
  (assessment.questions || []).find((q) => q.questionType === 'Passage' && q.passage)?.passage || '';

// Preview order matches the generation plan (automaticQuestionPlan): Direct first,
// then Scenario, then the Image group and finally the Passage group. The shared
// image and shared passage are rendered immediately above the questions that use
// them rather than at the top of the assessment.
const TYPE_ORDER = ['Direct', 'Scenario', 'Image', 'Passage'];

/**
 * Groups an assessment's questions into the canonical category order, keeping the
 * original order within each group. Any unrecognised category is appended at the
 * end so a question can never be dropped from the preview.
 */
const groupQuestionsByType = (questions = []) => {
  const typeOfQuestion = (q) => q.questionType || q.mimeType || q.type || 'Direct';
  const groups = TYPE_ORDER
    .map((type) => ({ type, items: questions.filter((q) => typeOfQuestion(q) === type) }))
    .filter((group) => group.items.length > 0);
  const other = questions.filter((q) => !TYPE_ORDER.includes(typeOfQuestion(q)));
  return other.length ? [...groups, { type: 'Other', items: other }] : groups;
};

/**
 * Full draft preview for the fully-automatic flow: course + details, each
 * assessment + details, and all questions (any type) — organized, read-only.
 * Opens before anything is persisted so the user can review then Save/Discard.
 */
export default function AutoPreviewDialog({ open, onClose, course, assessments = [] }) {
  if (!course) return null;

  const totalQuestions = assessments.reduce((s, a) => s + (a.questions || []).length, 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <span>Preview draft — {course.courseName}</span>
        <Button onClick={onClose} size="small" sx={{ minWidth: 0, p: 0.5 }} aria-label="Close">
          <X size={18} />
        </Button>
      </DialogTitle>

      <DialogContent dividers>
        {/* ---- Course + details ---- */}
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <GraduationCap size={18} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{course.courseName}</Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} sx={{ my: 1, flexWrap: 'wrap', gap: 0.5 }}>
            {course.code && <Chip size="small" label={course.code} />}
            {course.courseType && <Chip size="small" label={course.courseType} />}
            {(course.gradeLabel || course.level) && <Chip size="small" label={course.gradeLabel || course.level} />}
            <Chip size="small" color="primary" label={`${assessments.length} assessment(s)`} />
            <Chip size="small" color="primary" variant="outlined" label={`${totalQuestions} question(s)`} />
          </Stack>
          {course.about && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{course.about}</Typography>
          )}
          <Typography variant="subtitle2" sx={{ mt: 1 }}>Domains</Typography>
          {(course.domains || []).map((d, i) => (
            <Box key={i} sx={{ mb: 0.5 }}>
              <Typography variant="body2"><b>{d.name}</b>{d.percentage != null ? ` (${d.percentage}%)` : ''}</Typography>
              {(d.subDomain || []).length > 0 && (
                <Box component="ul" sx={{ m: '2px 0 0 18px', p: 0 }}>
                  {d.subDomain.map((s, k) => (
                    <li key={k} style={{ fontSize: 12 }}>
                      <b>{s.name}</b>{s.subTopics ? ` — ${s.subTopics}` : ''}
                    </li>
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>

        <Divider />

        {/* ---- Assessments + their questions ---- */}
        {assessments.map((a, ai) => {
          const groups = groupQuestionsByType(a.questions || []);
          // Q numbers run continuously across the groups (Q1..Q10), so the reader
          // sees one sequence even though the questions are shown category by category.
          let questionNumber = 0;
          return (
          <Box key={ai} sx={{ mt: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ClipboardList size={16} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{a.name}</Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} sx={{ my: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
              {a.examCode && <Chip size="small" variant="outlined" label={a.examCode} />}
              {a.examTime && <Chip size="small" variant="outlined" label={`${a.examTime} min`} />}
              {a.totalMarks && <Chip size="small" variant="outlined" label={`${a.totalMarks} marks`} />}
              {/* Exactly one Bloom's level per assessment. */}
              {a.assessmentLevel && (
                <Chip size="small" color="secondary" label={`Bloom: ${a.assessmentLevel}`} />
              )}
              <Chip size="small" color="primary" label={`${(a.questions || []).length} question(s)`} />
            </Stack>

            {(a.questions || []).length === 0 && (
              <Typography variant="caption" color="text.secondary">No questions generated for this assessment.</Typography>
            )}

            {groups.map((group) => (
              <Box key={group.type}>
                {/* The single shared passage / image sits directly above the questions
                    that reference it, so the reader meets it in reading order. */}
                {group.type === 'Passage' && sharedPassageOf(a) && (
                  <PassageBlock
                    passage={sharedPassageOf(a)}
                    caption={`Shared passage — the ${group.items.length} question(s) below refer to it`}
                  />
                )}

                {group.items.map((q) => {
                  questionNumber += 1;
                  const qi = questionNumber - 1;
                  return (
              <Box key={qi} sx={{ mt: 1.25, pl: 1.25, borderLeft: '3px solid #ece9ff' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>Q{qi + 1}.</Typography>
                  <Chip size="small" variant="outlined" label={typeOf(q)} />
                </Stack>
                {/* One image per image question, rendered directly ABOVE its question
                    so the question reads beneath the image it refers to. */}
                {q.questionType === 'Image' && (q.asset ? (
                  <Box
                    component="img"
                    src={q.asset}
                    alt={`Question ${qi + 1} image`}
                    sx={{
                      display: 'block',
                      width: '100%',
                      maxWidth: 520,
                      maxHeight: 320,
                      objectFit: 'contain',
                      mt: 0.5,
                      mb: 1,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.default',
                    }}
                  />
                ) : (
                  <Box sx={{ mt: 0.5, mb: 1, p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
                    <Typography variant="caption" color="error">
                      Image generation was unavailable. Regenerate this draft to create the visual.
                    </Typography>
                  </Box>
                ))}
                <Typography variant="body2">{q.question}</Typography>
                {/* Any non-image asset still renders inline, after the question. */}
                {q.asset && q.questionType !== 'Image' && (
                  <Box
                    component="img"
                    src={q.asset}
                    alt={`Question ${qi + 1} visual`}
                    sx={{
                      display: 'block',
                      width: '100%',
                      maxWidth: 520,
                      maxHeight: 320,
                      objectFit: 'contain',
                      mt: 1,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.default',
                    }}
                  />
                )}
                {q.passage && q.questionType !== 'Passage' && (
                  <Box sx={{ mt: 0.75, p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.25 }}>
                      {q.questionType === 'Scenario' ? 'Scenario' : 'Passage'}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {q.passage}
                    </Typography>
                  </Box>
                )}
                {Array.isArray(q.answer) && (
                  <Box component="ul" sx={{ m: '4px 0 0 18px', p: 0 }}>
                    {q.answer.map((opt, oi) => (
                      <li
                        key={oi}
                        style={{
                          fontSize: 12.5,
                          color: opt.is_correct === 1 ? '#1b7f4b' : '#1c1c28',
                          fontWeight: opt.is_correct === 1 ? 700 : 400,
                        }}
                      >
                        {opt.answer}{opt.is_correct === 1 ? '  ✓' : ''}
                      </li>
                    ))}
                  </Box>
                )}
                {q.explanation && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                    <b>Why:</b> {q.explanation}
                  </Typography>
                )}
                {q.hint && (
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                    <b>Hint:</b> {q.hint}
                  </Typography>
                )}
              </Box>
                  );
                })}
              </Box>
            ))}

            {ai < assessments.length - 1 && <Divider sx={{ mt: 2 }} />}
          </Box>
          );
        })}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
